import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import { BASE_ASSETS_URL } from "constants/constants";

const CLMHeroData = {
  // title: "REPOSITORY",
  heading: "Centralize every contract in one smart hub",
  description:
    "A secure, searchable space to store, manage, and access contracts, backed by version control, audit logs, and smart filters for your legal and business teams.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/repository-hero-1.png`,
  btnText: "Take a Quick Demo",
  btnUrl: "/clm/request-demo",
};

const faqData = [
  {
    question:
      "What is the BoloSign Repository, and why does it matter for enterprises?",
    answer:
      "The BoloSign Repository is your centralized workspace for storing, organizing, and managing every contract across departments. It ensures your legal, procurement, and finance teams always have real-time access to the latest agreements — no more scattered folders or version confusion.",
  },
  {
    question: "How does BoloSign handle contract metadata?",
    answer:
      "Our repository automatically pulls and categorizes over 1,000 types of metadata using AI — including key fields like effective date, contract type, owner, deal value, and renewal terms. This makes searching, reporting, and auditing fast and effortless.",
  },
  {
    question:
      "Can we control who can access or edit contracts in the repository?",
    answer:
      "Absolutely. You can set granular, role-based permissions — define who can view, edit, approve, or just comment. Whether it's a legal head or a sales team manager, you control who sees what, and when.",
  },
  {
    question: "Is the repository searchable across all contract contents?",
    answer:
      "Yes. Our full-text search lets you instantly find contracts by keywords, clauses, metadata, or even party names. You can filter by status (e.g., Draft, Executed), type (e.g., MSA, NDA), owner, or date — even across thousands of documents.",
  },
  {
    question: "How does version tracking work in BoloSign Repository?",
    answer:
      "Every contract version is automatically saved and labeled. You can easily compare changes, restore past versions, and track who made updates. This gives you complete visibility and reduces legal risks due to outdated terms.",
  },
  {
    question: "Can I generate reports directly from the repository?",
    answer:
      "Yes. You can save custom views and schedule reports based on filters like expiring contracts, high-value deals, or pending approvals. These reports can be shared with finance, legal, or procurement in just a few clicks.",
  },
  {
    question: "Is the repository compliant and audit-ready?",
    answer:
      "Definitely. BoloSign Repository is built to meet enterprise-grade security standards (SOC 2, ISO 27001). Every action is logged in an audit trail, and metadata is timestamped — so you're always ready for internal reviews or external audits.",
  },
];

const Repository = () => {
  return (
    <>
      <Head>
        <title>BoloSign CLM - Contract Repository</title>
        <meta
          name="description"
          content="Centralize every contract in one smart hub with BoloSign CLM. Secure, searchable, and backed by version control and audit logs for your legal and business teams."
        />
        <meta
          name="keywords"
          content="BoloSign Contract Repository, CLM repository, contract management, contract storage, contract version control, contract audit logs, enterprise contract management"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/repository/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <EqualDivisionSection
        // title=""
        heading="Smart, centralized contract management"
        description="BoloSign centralizes your contracts, automatically enriches metadata with AI, and ensures quick, secure access to essential contract details."
        checks={[
          "Auto-extract 1,000+ metadata fields.",
          "Minimize manual work and review time.",
          "Instantly link related contracts and addenda.",
        ]}
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/repository-agreement.gif`}
        video="/videos/repository-smart.mp4"
        bgColor=""
        imageRight={true}
        leftDivStyle="items-center md:-mt-28 md:-mb-32"
      />

      <EqualDivisionSection
        // title=""
        heading="Find contracts in seconds"
        description="Quickly locate any contract with full-text search, advanced filters, and smart views, so that your team saves time."
        checks={[
          "Search across full contract text, not just titles.",
          "Filter by key metadata like value, expiry, or parties.",
          "Save custom views for quick access anytime.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/repository-exclusion-1.png`}
        bgColor="bg-cream"
        imageRight={false}
      />

      <EqualDivisionSection
        // title=""
        heading="Automate contract insights"
        description="Get quick access to contract data, spot trends, and deliver reports without the manual work."
        checks={[
          "Save smart views for instant access to key contract data.",
          "Schedule recurring reports from saved filters and searches.",
          "Email insights directly to finance, legal, and business teams.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/repository-contract-expiring-1.png`}
        bgColor="bg-dark"
        imageRight={true}
      />

      <EqualDivisionSection
        // title=""
        heading="Secure contract access, always"
        description="We protect your data with enterprise-grade security and give you full control over who sees what."
        checks={[
          "End-to-end encryption ensures sensitive contract data stays protected.",
          "Role-based permissions to manage who can access or edit documents.",
          "Fast, auditable access for compliance, audits, and internal reviews.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/repository-settings-1.png`}
        bgColor=""
        imageRight={false}
      />

      {/* FAQ Section */}
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={faqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default Repository;
